/**
 * Material Icon Texture Keys
 *
 * Maps icon names to Phaser texture keys for Material Icon SVG files.
 * These SVG files are loaded in BootScene from /public/icons/material/.
 *
 * Usage:
 * ```ts
 * import { ICONS } from '@/config/icons';
 *
 * // In GlassButton
 * new GlassButton(this, x, y, {
 *   text: 'PLAY',
 *   iconKey: ICONS.PLAY_CIRCLE,
 * });
 *
 * // As standalone image
 * this.add.image(x, y, ICONS.TROPHY).setDisplaySize(32, 32);
 * ```
 */

export const ICONS = {
  // UI Actions
  TROPHY: 'icon-trophy',
  TROPHY_GOLD: 'icon-trophy-gold',
  SETTINGS: 'icon-settings',
  INSTALL_MOBILE: 'icon-install',
  EDIT: 'icon-edit',
  PAUSE: 'icon-pause',
  PLAY_CIRCLE: 'icon-play-circle',
  PLAY_CIRCLE_BLACK: 'icon-play-circle-black',
  TARGET: 'icon-target',
  STATISTICS: 'icon-statistics',
  TIMER: 'icon-timer',
  HEIGHT: 'icon-height',
  SHARE: 'icon-share',
  REFRESH: 'icon-refresh',
  WIFI_OFF: 'icon-wifi-off',
  STAR: 'icon-star',

  // Medals & Awards
  MEDAL: 'icon-medal',
  MEDAL_GOLD: 'icon-medal-gold',
  MEDAL_SILVER: 'icon-medal-silver',
  MEDAL_BRONZE: 'icon-medal-bronze',

  // Collectibles
  DIAMOND: 'icon-diamond',
  DIAMOND_SHINE: 'icon-diamond', // Alias for DIAMOND (same texture)
  COIN: 'icon-coin',

  // Navigation
  ARROW_BACK: 'icon-arrow-back',
  EXPLORE: 'icon-explore',
  INFO: 'icon-info',
  CLOSE: 'icon-close',
} as const;

/**
 * Helper to create icon+text displays outside of buttons
 * Returns a container with icon and text properly aligned
 */
export function createIconText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  iconKey: string,
  text: string,
  style: Partial<Phaser.Types.GameObjects.Text.TextStyle>,
  iconSize: number = 24
): Phaser.GameObjects.Container {
  const container = scene.add.container(x, y);

  // Create icon
  const icon = scene.add.image(0, 0, iconKey);
  icon.setDisplaySize(iconSize, iconSize);
  icon.setOrigin(0, 0.5);
  container.add(icon);

  // Create text
  const textObj = scene.add.text(iconSize + 8, 0, text, style);
  textObj.setOrigin(0, 0.5);
  container.add(textObj);

  return container;
}
